import React, { useState, useContext } from "react";

import {
  Button,
  CssBaseline,
  TextField,
  Typography,
  Box,
  InputAdornment,
  IconButton,
  Hidden,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";

import {
  Visibility,
  VisibilityOff,
  Group,
  Forum,
  FlashOn,
} from "@material-ui/icons";

import { makeStyles, useTheme } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";

import { AuthContext } from "../../context/Auth/AuthContext";
import logoBlack from "../../assets/whaticket-logo-black.png";
import logoWhite from "../../assets/whaticket-logo-white.png";

// SOMA ZapDesk brand palette (from logo)
const brandOrange = "#C07A1B";
const brandOrangeDark = "#9A5F13";
const brandInk = "#232327";
const brandPaper = "#F1F2F4";
const brandMuted = "#6B7280";
const brandLime = "#9DBE3C";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    display: "flex",
    backgroundColor: brandPaper,
  },
  brand: {
    flex: "0 0 46%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    padding: theme.spacing(7),
    color: "#FFFFFF",
    background: `radial-gradient(900px 480px at 85% -5%, rgba(192,122,27,0.38), transparent 60%), radial-gradient(700px 500px at -10% 110%, rgba(157,190,60,0.12), transparent 55%), linear-gradient(155deg, #1B1B1F 0%, #2E2413 58%, #4A3413 100%)`,
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  brandBadge: {
    backgroundColor: "#F4F5F6",
    borderRadius: 20,
    padding: theme.spacing(3, 4),
    width: 300,
    maxWidth: "80%",
    boxShadow: "0 18px 50px rgba(0,0,0,0.45)",
    marginBottom: theme.spacing(4),
  },
  brandBadgeImg: {
    width: "100%",
    display: "block",
  },
  brandName: {
    fontWeight: 800,
    letterSpacing: 4,
    fontSize: 15,
    color: brandOrange,
    marginBottom: theme.spacing(3),
  },
  brandFeature: {
    display: "flex",
    alignItems: "center",
    textAlign: "left",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
    color: "rgba(255,255,255,0.88)",
    fontSize: 15,
  },
  brandFeatureIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 38,
    height: 38,
    borderRadius: 12,
    backgroundColor: "rgba(192,122,27,0.18)",
    border: "1px solid rgba(192,122,27,0.45)",
    color: "#E8A93D",
    flexShrink: 0,
  },
  formSide: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4, 3),
  },
  mobileLogo: {
    width: 220,
    maxWidth: "70%",
    marginBottom: theme.spacing(3),
  },
  card: {
    width: 420,
    maxWidth: "100%",
    backgroundColor: "#FFFFFF",
    borderRadius: 18,
    boxShadow: "0 20px 60px rgba(35,35,39,0.12)",
    overflow: "hidden",
  },
  accentBar: {
    height: 5,
    background: `linear-gradient(90deg, ${brandOrange} 0%, #E8A93D 55%, ${brandLime} 100%)`,
  },
  cardBody: {
    padding: theme.spacing(4.5, 4),
  },
  title: {
    fontWeight: 800,
    color: brandInk,
    fontSize: 24,
  },
  subtitle: {
    color: brandMuted,
    fontSize: 14,
    marginTop: theme.spacing(0.75),
    marginBottom: theme.spacing(3),
  },
  form: {
    width: "100%",
  },
  submit: {
    margin: theme.spacing(2.5, 0, 1),
    padding: theme.spacing(1.4),
    borderRadius: 10,
    fontWeight: 700,
    fontSize: 15,
    textTransform: "none",
    backgroundColor: brandOrange,
    color: "#FFFFFF",
    boxShadow: "0 10px 24px rgba(192,122,27,0.35)",
    "&:hover": {
      backgroundColor: brandOrangeDark,
      boxShadow: "0 12px 28px rgba(154,95,19,0.4)",
    },
  },
  footer: {
    marginTop: theme.spacing(3),
    color: brandMuted,
    fontSize: 12,
    letterSpacing: 1.5,
    fontWeight: 600,
  },
}));

const Login = () => {
  const classes = useStyles();
  const theme = useTheme();

  const [rememberMe, setRememberMe] = useState(
    () => window.localStorage.getItem("somaZapDesk.rememberMe") === "1"
  );
  const [user, setUser] = useState(() => ({
    email:
      window.localStorage.getItem("somaZapDesk.rememberMe") === "1"
        ? window.localStorage.getItem("somaZapDesk.rememberEmail") || ""
        : "",
    password: "",
  }));
  const [showPassword, setShowPassword] = useState(false);

  const { handleLogin } = useContext(AuthContext);

  const handleChangeInput = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handlSubmit = (e) => {
    e.preventDefault();
    if (rememberMe) {
      window.localStorage.setItem("somaZapDesk.rememberMe", "1");
      window.localStorage.setItem("somaZapDesk.rememberEmail", user.email);
    } else {
      window.localStorage.removeItem("somaZapDesk.rememberMe");
      window.localStorage.removeItem("somaZapDesk.rememberEmail");
    }
    handleLogin(user);
  };

  const logo = theme.palette.type === "dark" ? logoWhite : logoBlack;
  const features = [
    { icon: <Group />, text: i18n.t("login.brand.f1") },
    { icon: <Forum />, text: i18n.t("login.brand.f2") },
    { icon: <FlashOn />, text: i18n.t("login.brand.f3") },
  ];

  return (
    <div className={classes.root}>
      <CssBaseline />
      <Hidden smDown>
        <aside className={classes.brand}>
          <div className={classes.brandBadge}>
            <img
              src={logoWhite}
              alt="SOMA ZapDesk"
              className={classes.brandBadgeImg}
            />
          </div>
          <div className={classes.brandName}>SOMA ZAPDESK</div>
          <div>
            {features.map((f, i) => (
              <div className={classes.brandFeature} key={i}>
                <span className={classes.brandFeatureIcon}>{f.icon}</span>
                <span>{f.text}</span>
              </div>
            ))}
          </div>
        </aside>
      </Hidden>
      <main className={classes.formSide}>
        <Hidden mdUp>
          <img src={logo} alt="SOMA ZapDesk" className={classes.mobileLogo} />
        </Hidden>
        <div className={classes.card}>
          <div className={classes.accentBar} />
          <div className={classes.cardBody}>
            <Typography className={classes.title}>
              {i18n.t("login.title")}
            </Typography>
            <Typography className={classes.subtitle}>
              {i18n.t("login.subtitle")}
            </Typography>
            <form className={classes.form} noValidate onSubmit={handlSubmit}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="email"
                label={i18n.t("login.form.email")}
                name="email"
                value={user.email}
                onChange={handleChangeInput}
                autoComplete="email"
                autoFocus
              />
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                name="password"
                label={i18n.t("login.form.password")}
                id="password"
                value={user.password}
                onChange={handleChangeInput}
                autoComplete="current-password"
                type={showPassword ? "text" : "password"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((e) => !e)}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    name="rememberMe"
                    style={{ color: brandOrange }}
                  />
                }
                label={i18n.t("login.rememberMe")}
                style={{ marginTop: 4, color: brandMuted }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                className={classes.submit}
              >
                {i18n.t("login.buttons.submit")}
              </Button>
            </form>
          </div>
        </div>
        <div className={classes.footer}>SOMA ZAPDESK</div>
      </main>
    </div>
  );
};

export default Login;
